import { displayDigits } from '../utils/displayDigits';
import React, { useMemo } from 'react';

const START_DEG = 135;
const TOTAL_DEG = 270;
const SEGMENTS = 48;
const GRAD_STOPS = ['#E040FB', '#7B68EE', '#448AFF', '#00D2FF', '#40C4FF'];

function hexToRgb(hex: string) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : { r: 0, g: 0, b: 0 };
}

function lerpColor(t: number, stops: string[]): string {
  const n = stops.length - 1;
  const i = Math.min(Math.floor(t * n), n - 1);
  const lt = t * n - i;
  const a = hexToRgb(stops[i]);
  const b = hexToRgb(stops[i + 1]);
  return `rgb(${Math.round(a.r + (b.r - a.r) * lt)},${Math.round(a.g + (b.g - a.g) * lt)},${Math.round(a.b + (b.b - a.b) * lt)})`;
}

function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcSegPath(cx: number, cy: number, r: number, a1: number, a2: number): string {
  const s = polar(cx, cy, r, a1);
  const e = polar(cx, cy, r, a2);
  return `M ${s.x.toFixed(3)} ${s.y.toFixed(3)} A ${r} ${r} 0 0 1 ${e.x.toFixed(3)} ${e.y.toFixed(3)}`;
}

function buildTrackPath(cx: number, cy: number, r: number): string {
  const mid = START_DEG + TOTAL_DEG / 2;
  const end = START_DEG + TOTAL_DEG;
  const s = polar(cx, cy, r, START_DEG);
  const m = polar(cx, cy, r, mid);
  const e = polar(cx, cy, r, end);
  const f = (n: number) => n.toFixed(3);
  return `M ${f(s.x)} ${f(s.y)} A ${r} ${r} 0 0 1 ${f(m.x)} ${f(m.y)} A ${r} ${r} 0 0 1 ${f(e.x)} ${f(e.y)}`;
}

export interface GradientGaugeProps {
  score: number;
  size?: number;
  scoreLabel?: string;
  centerValue?: string | number;
  subtitle?: string;
  hidePercent?: boolean;
}

export function GradientGauge({
  score,
  size = 140,
  scoreLabel,
  centerValue,
  subtitle,
  hidePercent = false,
}: GradientGaugeProps) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.38;
  const sw = size * 0.085;

  const clamped = Math.max(0, Math.min(100, score));
  const filledDeg = (clamped / 100) * TOTAL_DEG;
  const accent = lerpColor(clamped / 100, GRAD_STOPS);

  const trackPath = buildTrackPath(cx, cy, r);

  const segments = useMemo(() => {
    if (clamped === 0) return [];
    const segDeg = filledDeg / SEGMENTS;
    return Array.from({ length: SEGMENTS }, (_, i) => {
      const t = i / (SEGMENTS - 1);
      const a1 = START_DEG + i * segDeg;
      const a2 = a1 + segDeg + 0.4;
      return { path: arcSegPath(cx, cy, r, a1, a2), color: lerpColor(t, GRAD_STOPS) };
    });
  }, [clamped, cx, cy, r, filledDeg]);

  const mainText = centerValue ?? clamped;
  const caption = subtitle ?? scoreLabel;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0, margin: '0 auto' }}>
      <svg width={size} height={size} style={{ display: 'block' }}>
        <path d={trackPath} stroke="var(--surface-sunken)" strokeWidth={sw} fill="none" strokeLinecap="round" />
        {segments.map((seg, i) => (
          <path
            key={i}
            d={seg.path}
            stroke={seg.color}
            strokeWidth={sw}
            fill="none"
            strokeLinecap={i === segments.length - 1 ? 'round' : 'butt'}
          />
        ))}
        {clamped > 0 && (
          <path
            d={arcSegPath(cx, cy, r, START_DEG, START_DEG + 0.1)}
            stroke="#E040FB"
            strokeWidth={sw}
            fill="none"
            strokeLinecap="round"
          />
        )}
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            fontSize: size >= 160 ? 34 : centerValue != null ? 42 : 28,
            fontWeight: 800,
            color: centerValue != null ? accent : 'var(--fg-1)',
            fontFamily: 'var(--font-display)',
            lineHeight: 1,
            transition: 'color 280ms ease-out',
          }}
        >
          {displayDigits(mainText)}
          {!hidePercent && centerValue == null && '%'}
        </span>
        {caption && (
          <span
            style={{
              fontSize: centerValue != null ? 10 : 9,
              color: 'var(--fg-4)',
              fontWeight: 700,
              letterSpacing: '0.05em',
              marginTop: centerValue != null ? 6 : 2,
              textTransform: centerValue != null ? 'none' : 'uppercase',
              textAlign: 'center',
              maxWidth: '80%',
            }}
          >
            {caption}
          </span>
        )}
      </div>
    </div>
  );
}
